import { REACT_ENV } from '@/Config';
import { ReactComponent as AllowIcon } from '@icons/allow.svg';
import { ReactComponent as RestrictIcon } from '@icons/restrict.svg';
import { ReactComponent as BlockIcon } from '@icons/restrictred.svg';
import { Option } from '@/components/FormElement/types';

const CONSUMER_TRUST_INDEX = 'CONSUMER_TRUST_INDEX';
const PAYEE_TRUST_INDEX = 'PAYEE_TRUST_INDEX';
const CHECKOUT_AND_REFUND = 'CHECKOUT_AND_REFUND';
const RETURNS_ORCHESTRATOR = 'RETURNS_ORCHESTRATOR';
const IDENTITY_AND_AUTH = 'IDENTITY_AND_AUTH';
const PAYMENT_PERSONALIZATION = 'PAYMENT_PERSONALIZATION';

export type config_type = 'LOW_RISK' | 'MEDIUM_RISK' | 'HIGH_RISK';

export type IConfigType = 'HIGH_TRUST' | 'MEDIUM_TRUST' | 'LOW_TRUST';

export const ORGANIZATION_TYPES = Object.freeze({
  NETWORK_MASTER: 'NETWORK_MASTER',
  NETWORK: 'NETWORK',
  NODE: 'NODE',
});

export const ROLES = Object.freeze({
  ADMIN: 'Admin',
  EDITOR: 'Editor',
  VIEWER: 'Viewer',
});

export const TransactionStatus = Object.freeze({
  Successful: 'Successful',
  Pending: 'Pending',
  Declined: 'Declined',
});

export const FraudRiskTypeStatus = Object.freeze({
  ACTIVE: 'ACTIVE',
  COMING_SOON: 'COMING_SOON',
});

export const PUBLIC_NAVIGATION: { [key: string]: string } = Object.freeze({
  login: '/login',
  verifySecureCode: '/verify-securecode',
  signUp: '/signup/:code',
  confirmEmail: '/confirm-email',
  signUpSuccess: '/signup-success',
  enrollment: '/enrollment',
  useCases: 'use-case',
  resetPassword: '/reset-password',
  resetEmailVerify: '/verify-reset-email',
  setNewPassword: '/set-new-password/:token',
  resetPasswordSuccess: '/reset-password-success',
  readmeDocs: '/readme-docs',
  projectInvite: '/project-invite/:token',
});

export const IMAGE_SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];

export const FeaturesEnum = {
  Organization: 'Organisation',
  Users: 'Users',
  Roles: 'Roles',
  Projects: 'Projects',
  Dashboard: 'Dashboard',
  Integrations: 'Integrations',
  Contract: 'Contract',
};

export const rolePermissionEnum = {
  [FeaturesEnum.Organization]: {
    ViewNetwork: 'View Network',
    UpdateNetwork: 'Update Network',
    DeleteNetwork: 'Delete Network',
    CreateNetwork: 'Create Network',
    CreateNode: 'Create Node',
    ViewNode: 'View Node',
    UpdateNode: 'Update Node',
    DeleteNode: 'Delete Node',
  },
  [FeaturesEnum.Users]: {
    CreateTeam: 'Create Team',
    ViewTeam: 'View Team',
    UpdateTeam: 'Update Team',
    DeleteTeam: 'Delete Team',
    CreateUser: 'Create User',
    ViewUser: 'View User',
    UpdateUser: 'Update User',
    DeleteUser: 'Delete User',
  },
  [FeaturesEnum.Projects]: {
    CreateProject: 'Create Project',
    ViewProject: 'View Project',
    UpdateProject: 'Update Project',
    DeleteProject: 'Delete Project',
  },
  [FeaturesEnum.Dashboard]: {
    ViewOrganizations: 'View organizations',
    ViewListOfConsumers: 'View list of consumers',
    ViewDashboard: 'View Dashboard',
  },
  [FeaturesEnum.Roles]: {
    CreateRole: 'Create Role',
    UpdateRole: 'Update Role',
    ViewRole: 'View Role',
    DeleteRole: 'Delete Role',
  },
  [FeaturesEnum.Contract]: {
    CreateContract: 'Create Contract',
    ViewContract: 'View Contract',
    UpdateContract: 'Update Contract',
    DeleteContract: 'Delete Contract',
  },
  [FeaturesEnum.Integrations]: {
    CreateService: 'Create Service',
    ViewService: 'View Service',
    UpdateService: 'Update Service',
    DeleteService: 'Delete Service',
  },
};

export const PRIVATE_NAVIGATION = Object.freeze({
  consumerTrustGuide: {
    activeSidebar: -1,
    path: '/consumer-trust-index-guide',
    feature: true,
    permission: true,
    organization: true,
  },
  auth: {
    signup: {
      activeSidebar: 2,
      path: '/signup/:code',
      feature: true,
      permission: true,
      organization: true,
    },
    signupsuccess: {
      activeSidebar: 2,
      path: '/signup-success',
      feature: true,
      permission: true,
      organization: true,
    },
    projectinvite: {
      activeSidebar: 2,
      path: '/project-invite/:token',
      feature: true,
      permission: true,
      organization: true,
    },
  },
  useCase: {
    list: {
      activeSidebar: 2,
      path: '/use-case',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NODE,
    },
    product: {
      activeSidebar: 2,
      path: '/use-case/:product',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NODE,
    },
  },
  home: {
    activeSidebar: 1,
    path: '/home',
    feature: true,
    permission: true,
    organization: true,
  },
  users: {
    list: {
      activeSidebar: 4,
      path: '/users',
      feature: FeaturesEnum.Users,
      permission: rolePermissionEnum[FeaturesEnum.Users].ViewUser,
      organization: true,
    },
    view: {
      activeSidebar: 4,
      path: '/users/:id',
      feature: FeaturesEnum.Users,
      permission: rolePermissionEnum[FeaturesEnum.Users].ViewUser,
      organization: true,
    },
  },
  teams: {
    list: {
      activeSidebar: 4,
      path: '/teams',
      feature: true,
      permission: true,
      organization: true,
    },
    view: {
      activeSidebar: 4,
      path: '/teams/:id',
      feature: true,
      permission: true,
      organization: true,
    },
  },
  groups: {
    list: {
      activeSidebar: 4,
      path: '/groups',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NODE,
    },
    view: {
      activeSidebar: 4,
      path: '/groups:id',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NODE,
    },
    roles: {
      activeSidebar: 4,
      path: '/groups:id/roles',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NODE,
    },
  },
  projects: {
    list: {
      activeSidebar: 2,
      path: '/projects',
      feature: FeaturesEnum.Projects,
      permission: rolePermissionEnum[FeaturesEnum.Projects].ViewProject,
      organization: ORGANIZATION_TYPES.NODE,
    },
    add: {
      activeSidebar: 0,
      path: '/projects/new',
      feature: FeaturesEnum.Projects,
      permission: rolePermissionEnum[FeaturesEnum.Projects].CreateProject,
      organization: ORGANIZATION_TYPES.NODE,
    },
    edit: {
      activeSidebar: 0,
      path: '/projects/edit',
      feature: FeaturesEnum.Projects,
      permission: rolePermissionEnum[FeaturesEnum.Projects].UpdateProject,
      organization: ORGANIZATION_TYPES.NODE,
    },
    view: {
      activeSidebar: 0,
      path: '/projects/view',
      feature: FeaturesEnum.Projects,
      permission: rolePermissionEnum[FeaturesEnum.Projects].ViewProject,
      organization: ORGANIZATION_TYPES.NODE,
    },
    invite: {
      activeSidebar: 0,
      path: '/projects/invite',
      feature: FeaturesEnum.Projects,
      permission: rolePermissionEnum[FeaturesEnum.Projects].CreateProject,
      organization: ORGANIZATION_TYPES.NODE,
    },
    setting: {
      path: '/projects/settings',
      feature: FeaturesEnum.Projects,
      permission: rolePermissionEnum[FeaturesEnum.Projects].UpdateProject,
      organization: ORGANIZATION_TYPES.NODE,
    },
    sti: {
      getStarted: {
        activeSidebar: 0,
        path: '/projects/sti/get-started',
        feature: FeaturesEnum.Projects,
        permission: rolePermissionEnum[FeaturesEnum.Projects].CreateProject,
        organization: ORGANIZATION_TYPES.NODE,
      },
    },
    cr: {
      getStarted: {
        activeSidebar: 2,
        path: '/projects/cr/get-started',
        feature: FeaturesEnum.Projects,
        permission: rolePermissionEnum[FeaturesEnum.Projects].CreateProject,
        organization: ORGANIZATION_TYPES.NODE,
      },
    },
    configure: {
      activeSidebar: 0,
      path: '/projects/configure',
      feature: FeaturesEnum.Projects,
      permission: rolePermissionEnum[FeaturesEnum.Projects].CreateProject,
      organization: ORGANIZATION_TYPES.NODE,
    },
    manage: {
      activeSidebar: 2,
      path: '/projects/manage',
      feature: FeaturesEnum.Projects,
      permission: rolePermissionEnum[FeaturesEnum.Projects].ViewProject,
      organization: ORGANIZATION_TYPES.NODE,
    },
    trustThresholdSensitivityGraph: {
      activeSidebar: 0,
      path: '/projects/trust-threshold-sensitivity-graph',
      feature: FeaturesEnum.Projects,
      permission: rolePermissionEnum[FeaturesEnum.Projects].CreateProject,
      organization: ORGANIZATION_TYPES.NODE,
    },
  },
  setupPayment: {
    list: {
      activeSidebar: 2,
      path: '/setup-payment',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NODE,
    },
    bankLogin: {
      activeSidebar: 2,
      path: '/setup-payment/bank-login',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NODE,
    },
    bankConsent: {
      activeSidebar: 2,
      path: '/setup-payment/bank-consent',
      organization: ORGANIZATION_TYPES.NODE,
      feature: true,
      permission: true,
    },
    setupSuccess: {
      activeSidebar: 2,
      path: '/setup-payment/setup-success',
      organization: ORGANIZATION_TYPES.NODE,
      feature: true,
      permission: true,
    },
    setupDeclined: {
      activeSidebar: 2,
      path: '/setup-payment/setup-declined',
      organization: ORGANIZATION_TYPES.NODE,
      feature: true,
      permission: true,
    },
  },
  organization: {
    list: {
      activeSidebar: 3,
      path: '/network',
      feature: FeaturesEnum.Organization,
      permission: `${rolePermissionEnum[FeaturesEnum.Organization].ViewNetwork},${rolePermissionEnum[FeaturesEnum.Organization].ViewNode}`,
      organization: `${ORGANIZATION_TYPES.NETWORK_MASTER},${ORGANIZATION_TYPES.NETWORK}`,
    },
    organisation: {
      activeSidebar: 5,
      path: '/organisation',
      feature: true,
      permission: true,
      organization: true,
    },
    add: {
      activeSidebar: 3,
      path: '/organization/create',
      feature: FeaturesEnum.Organization,
      permission: `${rolePermissionEnum[FeaturesEnum.Organization].CreateNetwork},${rolePermissionEnum[FeaturesEnum.Organization].CreateNode}`,
      organization: `${ORGANIZATION_TYPES.NETWORK_MASTER},${ORGANIZATION_TYPES.NETWORK}`,
    },
    setup: {
      activeSidebar: -1,
      path: '/organization/setup',
      feature: true,
      permission: true,
      organization: true,
    },
    inviteAdmin: {
      activeSidebar: 3,
      path: '/organization/invite-admin',
      feature: FeaturesEnum.Organization,
      permission: `${rolePermissionEnum[FeaturesEnum.Organization].CreateNetwork},${rolePermissionEnum[FeaturesEnum.Organization].CreateNode}`,
      organization: `${ORGANIZATION_TYPES.NETWORK_MASTER},${ORGANIZATION_TYPES.NETWORK}`,
    },
    invite: {
      activeSidebar: 3,
      path: '/organization/invite',
      feature: FeaturesEnum.Organization,
      permission: `${rolePermissionEnum[FeaturesEnum.Organization].CreateNetwork},${rolePermissionEnum[FeaturesEnum.Organization].CreateNode}`,
      organization: `${ORGANIZATION_TYPES.NETWORK_MASTER},${ORGANIZATION_TYPES.NETWORK}`,
    },
    partners: {
      activeSidebar: 6,
      path: '/partners',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NETWORK_MASTER,
    },
    partnerDetail: {
      activeSidebar: 6,
      path: '/partners/:id',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NETWORK_MASTER,
    },
  },
  contactSupport: {
    activeSidebar: 8,
    path: '/contact-support',
    feature: true,
    permission: true,
    organization: true,
  },
  onboarding: {
    ConsumerTrustNetwork: {
      activeSidebar: 0,
      path: '/onboard-into-consumer-trust-network-sandbox',
      feature: true,
      permission: true,
      organization: true,
    },
    uploadHistoricalData: {
      activeSidebar: 0,
      path: '/upload-historical-data',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NODE,
    },
    ctiConfirmation: {
      activeSidebar: 0,
      path: '/cti-confirmation',
      feature: true,
      permission: true,
      organization: ORGANIZATION_TYPES.NODE,
    },
  },
  demoMenu: {
    demo: {
      activeSidebar: 0,
      path: '/demo',
      feature: true,
      permission: true,
      organization: true,
    },
    overview: {
      activeSidebar: 0,
      path: '/overview',
      feature: true,
      permission: true,
      organization: true,
    },
    selectDemo: {
      activeSidebar: 0,
      path: '/select-demo',
      feature: true,
      permission: true,
      organization: true,
    },
    valueProposition: {
      activeSidebar: 0,
      path: '/value-proposition',
      feature: true,
      permission: true,
      organization: true,
    },
    solutions: {
      activeSidebar: 0,
      path: '/solutions',
      feature: true,
      permission: true,
      organization: true,
    },
    exploreDemo: {
      activeSidebar: 0,
      path: '/explore-demo',
      feature: true,
      permission: true,
      organization: true,
    },
    dataSharing: {
      activeSidebar: 0,
      path: '/data-sharing',
      feature: true,
      permission: true,
      organization: true,
    },
    useCases: {
      activeSidebar: 0,
      path: '/use-cases',
      feature: true,
      permission: true,
      organization: true,
    },
    contactUs: {
      activeSidebar: 0,
      path: '/contact-us',
      feature: true,
      permission: true,
      organization: true,
    },
  },
});

export {
  CHECKOUT_AND_REFUND,
  CONSUMER_TRUST_INDEX,
  IDENTITY_AND_AUTH,
  PAYEE_TRUST_INDEX,
  PAYMENT_PERSONALIZATION,
  RETURNS_ORCHESTRATOR,
};

export const projectSteps = (
  projectStepType: string = 'create',
  eventType: string = 'onboarding'
) => [
  {
    title: 'Pages',
    stages: [
      'Fraud typology',
      'Trust segments',
      ...(eventType === 'post-authorisation' ? ['Refund policy'] : []),
      'Summary',
      ...(projectStepType === 'create'
        ? ['Save', ...(REACT_ENV === 'production' ? ['Publish'] : [])]
        : []),
    ],
  },
];

export const DropDownOptions = Object.freeze({
  teams: [
    {
      label: 'General',
      value: 'General',
    },
    {
      label: 'Management',
      value: 'Management',
    },
  ],
  roles: [
    {
      label: 'Admin',
      value: 'Admin',
      description: 'Can fully configure and edit application',
    },
    {
      label: 'Editor',
      value: 'Editor',
      description: 'Can edit but cannot perform admin tasks',
    },
    {
      label: 'Viewer',
      value: 'Viewer',
      description: 'Can only view',
    },
  ],
  listBoxOptions: [
    {
      label: 'All',
      value: 'all',
    },
    {
      label: '/api/networksearch',
      value: '/api/networksearch',
    },
    {
      label: '/api/projects',
      value: '/api/projects',
    },
    {
      label: '/api/fraudprofiles',
      value: '/api/fraudprofiles',
    },
    {
      label: '/api/trudocs',
      value: '/api/trudocs',
    },
    {
      label: '/api/shoppertrustindex',
      value: '/api/shoppertrustindex',
    },
  ],
  DateRangeOptions: [
    {
      value: 30,
      label: 'Last 30 Days',
    },
    {
      value: 20,
      label: 'Last 20 Days',
    },
    {
      value: 15,
      label: 'Last 15 Days',
    },
    {
      value: 10,
      label: 'Last 10 Days',
    },
    {
      label: 'Last 5 Days',
      value: 5,
    },
  ],
  AllRecentOptions: [
    { value: 'all', label: 'All' },
    { value: 'recent', label: 'Recent' },
  ],
  fraudRiskTypeList: [
    {
      label: '360° First-Party Fraud',
      value: '360° First-Party Fraud',
    },
    {
      label: 'Synthetic Identity, Syndicates and Muling',
      value: 'Synthetic Identity, Syndicates and Muling',
    },
    {
      label: 'Chargeback Fraud',
      value: 'Chargeback Fraud',
    },
    {
      label: 'Authorised Push Payment fraud and Scams',
      value: 'Authorised Push Payment fraud and Scams',
    },
    {
      label: 'Ecommerce Policy Abuse',
      value: 'Ecommerce Policy Abuse',
    },
    {
      label: 'Bonus, Loyalty and Promotion Abuse',
      value: 'Bonus, Loyalty and Promotion Abuse',
    },
    {
      label: 'Buy-Now-Pay-Later (BNPL) Fraud',
      value: 'Buy-Now-Pay-Later (BNPL) Fraud',
    },
  ],
  eventOptions: [
    {
      value: 'onboarding',
      label: 'Onboarding',
      description:
        'The process of registering and verifying new consumers, including account eligibility checks and detection of fake accounts or bonus abuse tactics.',
    },
    {
      value: 'pre-authorisation',
      label: 'Pre-authorisation',
      description:
        'The review of transactions before authorisation to predict the likelihood of first-party fraud.',
    },
    {
      value: 'post-authorisation',
      label: 'Post-purchase',
      description:
        'Refund decisioning for post-purchase issues such as items not received claims, disputed deliveries, or chargeback requests.',
    },
  ],
});

export const tabOptions = [
  'Organisation',
  'Users',
  'Projects',
  'Dashboard',
  'Roles',
  'Contract',
  'Integrations',
];

export const roleAndPermissionData: any = {
  [ROLES.ADMIN]: {
    organization: [
      {
        title: 'Create Network',
        description: 'Add a new Network organisation.',
        hasPermission: false,
      },
      {
        title: 'View Network',
        description: 'Browse through a Network organisation.',
        hasPermission: false,
      },
      {
        title: 'Update Network',
        description: 'Make changes to a Network organisation.',
        hasPermission: false,
      },
      {
        title: 'Delete Network',
        description: 'Permanently remove a Network organisation.',
        hasPermission: false,
      },
      {
        title: 'Create Node',
        description: 'Setup a new node organisation.',
        hasPermission: 'true',
      },
      {
        title: 'View Node',
        description: 'Browse through a node organisation.',
        hasPermission: 'true',
      },
      {
        title: 'Update Node',
        description: 'Make changes to a node organisation.',
        hasPermission: 'true',
      },
      {
        title: 'Delete Node',
        description: 'Permanently remove a node organisation.',
        hasPermission: 'true',
      },
    ],
    users: [
      {
        title: 'Create Team',
        description: 'Create a new team.',
        hasPermission: 'true',
      },
      {
        title: 'View Team',
        description: 'Browse through a team.',
        hasPermission: 'true',
      },
      {
        title: 'Update Team',
        description: 'Make changes to a team.',
        hasPermission: 'true',
      },
      {
        title: 'Delete Team',
        description: 'Permanently delete a team.',
        hasPermission: 'true',
      },
      {
        title: 'Create User',
        description: 'Create a new user.',
        hasPermission: 'true',
      },
      {
        title: 'View User',
        description: 'Browse through a user profile.',
        hasPermission: 'true',
      },
      {
        title: 'Update User',
        description: 'Make changes to a user.',
        hasPermission: 'true',
      },
      {
        title: 'Delete User',
        description: 'Permanently remove a user.',
        hasPermission: 'true',
      },
    ],
    projects: [
      {
        title: 'Create Project',
        description: 'Create a new project.',
        hasPermission: 'true',
      },
      {
        title: 'View Project',
        description: 'Browse through a project.',
        hasPermission: 'true',
      },
      {
        title: 'Update Project',
        description: 'Make changes to a project’s configuration.',
        hasPermission: 'true',
      },
      {
        title: 'Delete Project',
        description: 'Permanently delete a project.',
        hasPermission: 'true',
      },
    ],
    dashboard: [
      {
        title: 'View projects',
        description: 'Browse through projects.',
        hasPermission: 'true',
      },
      {
        title: 'View list of consumers',
        description: 'View consumers and their profiles on product tables.',
        hasPermission: 'true',
      },
    ],
    roles: [
      {
        title: 'Create Role',
        description: 'Create a new roles.',
        hasPermission: false,
      },
      {
        title: 'View Role',
        description: 'Browse through roles and permissions.',
        hasPermission: 'true',
      },
      {
        title: 'Update Role',
        description: 'Make changes to roles and permissions.',
        hasPermission: false,
      },
      {
        title: 'Delete Role',
        description: 'Permanently delete a role.',
        hasPermission: false,
      },
    ],
  },
  [ROLES.EDITOR]: {
    organization: [
      {
        title: 'Create Network',
        description: 'Add a new Network organisation.',
        hasPermission: false,
      },
      {
        title: 'View Network',
        description: 'Browse through a Network organisation.',
        hasPermission: false,
      },
      {
        title: 'Update Network',
        description: 'Make changes to a Network organisation.',
        hasPermission: false,
      },
      {
        title: 'Delete Network',
        description: 'Permanently remove a Network organisation.',
        hasPermission: false,
      },
      {
        title: 'Create Node',
        description: 'Setup a new node organisation.',
        hasPermission: false,
      },
      {
        title: 'View Node',
        description: 'Browse through a node organisation.',
        hasPermission: 'true',
      },
      {
        title: 'Update Node',
        description: 'Make changes to a node organisation.',
        hasPermission: 'true',
      },
      {
        title: 'Delete Node',
        description: 'Permanently remove a node organisation.',
        hasPermission: false,
      },
    ],
    users: [
      {
        title: 'Create Team',
        description: 'Create a new team.',
        hasPermission: 'true',
      },
      {
        title: 'View Team',
        description: 'Browse through a team.',
        hasPermission: 'true',
      },
      {
        title: 'Update Team',
        description: 'Make changes to a team.',
        hasPermission: 'true',
      },
      {
        title: 'Delete Team',
        description: 'Permanently delete a team.',
        hasPermission: 'true',
      },
      {
        title: 'Create User',
        description: 'Create a new user.',
        hasPermission: 'true',
      },
      {
        title: 'View User',
        description: 'Browse through a user profile.',
        hasPermission: 'true',
      },
      {
        title: 'Update User',
        description: 'Make changes to a user.',
        hasPermission: 'true',
      },
      {
        title: 'Delete User',
        description: 'Permanently remove a user.',
        hasPermission: 'true',
      },
    ],
    projects: [
      {
        title: 'Create Project',
        description: 'Create a new project.',
        hasPermission: 'true',
      },
      {
        title: 'View Project',
        description: 'Browse through a project.',
        hasPermission: 'true',
      },
      {
        title: 'Update Project',
        description: 'Make changes to a project’s configuration.',
        hasPermission: 'true',
      },
      {
        title: 'Delete Project',
        description: 'Permanently delete a project.',
        hasPermission: 'true',
      },
    ],
    dashboard: [
      {
        title: 'View projects',
        description: 'Browse through projects.',
        hasPermission: 'true',
      },
      {
        title: 'View list of consumers',
        description: 'View consumers and their profiles on product tables.',
        hasPermission: 'true',
      },
    ],
    roles: [
      {
        title: 'Create Role',
        description: 'Create a new roles.',
        hasPermission: false,
      },
      {
        title: 'View Role',
        description: 'Browse through roles and permissions.',
        hasPermission: 'true',
      },
      {
        title: 'Update Role',
        description: 'Make changes to roles and permissions.',
        hasPermission: false,
      },
      {
        title: 'Delete Role',
        description: 'Permanently delete a role.',
        hasPermission: false,
      },
    ],
  },
  [ROLES.VIEWER]: {
    organization: [
      {
        title: 'Create Network',
        description: 'Add a new Network organisation.',
        hasPermission: false,
      },
      {
        title: 'View Network',
        description: 'Browse through a Network organisation.',
        hasPermission: false,
      },
      {
        title: 'Update Network',
        description: 'Make changes to a Network organisation.',
        hasPermission: false,
      },
      {
        title: 'Delete Network',
        description: 'Permanently remove a Network organisation.',
        hasPermission: false,
      },
      {
        title: 'Create Node',
        description: 'Setup a new node organisation.',
        hasPermission: false,
      },
      {
        title: 'View Node',
        description: 'Browse through a node organisation.',
        hasPermission: 'true',
      },
      {
        title: 'Update Node',
        description: 'Make changes to a node organisation.',
        hasPermission: false,
      },
      {
        title: 'Delete Node',
        description: 'Permanently remove a node organisation.',
        hasPermission: false,
      },
    ],
    users: [
      {
        title: 'Create Team',
        description: 'Create a new team.',
        hasPermission: false,
      },
      {
        title: 'View Team',
        description: 'Browse through a team.',
        hasPermission: 'true',
      },
      {
        title: 'Update Team',
        description: 'Make changes to a team.',
        hasPermission: false,
      },
      {
        title: 'Delete Team',
        description: 'Permanently delete a team.',
        hasPermission: false,
      },
      {
        title: 'Create User',
        description: 'Create a new user.',
        hasPermission: false,
      },
      {
        title: 'View User',
        description: 'Browse through a user profile.',
        hasPermission: 'true',
      },
      {
        title: 'Update User',
        description: 'Make changes to a user.',
        hasPermission: false,
      },
      {
        title: 'Delete User',
        description: 'Permanently remove a user.',
        hasPermission: false,
      },
    ],
    projects: [
      {
        title: 'Create Project',
        description: 'Create a new project.',
        hasPermission: false,
      },
      {
        title: 'View Project',
        description: 'Browse through a project.',
        hasPermission: 'true',
      },
      {
        title: 'Update Project',
        description: 'Make changes to a project’s configuration.',
        hasPermission: false,
      },
      {
        title: 'Delete Project',
        description: 'Permanently delete a project.',
        hasPermission: false,
      },
    ],
    dashboard: [
      {
        title: 'View projects',
        description: 'Browse through projects.',
        hasPermission: 'true',
      },
      {
        title: 'View list of consumers',
        description: 'View consumers and their profiles on product tables.',
        hasPermission: 'true',
      },
    ],
    roles: [
      {
        title: 'Create Role',
        description: 'Create a new roles.',
        hasPermission: false,
      },
      {
        title: 'View Role',
        description: 'Browse through roles and permissions.',
        hasPermission: false,
      },
      {
        title: 'Update Role',
        description: 'Make changes to roles and permissions.',
        hasPermission: false,
      },
      {
        title: 'Delete Role',
        description: 'Permanently delete a role.',
        hasPermission: false,
      },
    ],
  },
};

export const ShopperConfigSequence = [
  'IDENTITY',
  'BANKING_HISTORY',
  'OPEN_BANKING',
  'HISTORICAL_DATA',
  'NETWORK_INTELLIGENCE',
  'THIRD_PARTY_DATA',
  'CHARGEBACKS',
  'RETURNS',
  'CLAIMS_OF_ITEMS_NOT_RECEIVED',
  'BNPL',
  'AUTHORISED_PUSH_PAYMENTS',
  'MULLING',
  'PROMOTION_BONUS_AND_LOYALTY_ABUSE',
  'AFFORDABILITY_AND_PAYMENTS',
  'CHARGEBACK_TRENDS',
  'RETURNS_TRENDS',
  'ITEM_NOT_RECEIVED_TRENDS',
  'BUY_NOW_PAY_LATER',
  'AUTHORISED_PUSH_PAYMENTS_FRAUD',
  'MULING',
  'BONUS_LOYALTY_AND_PROMOTIONS',
  'PAYMENTS_AND_AFFORDABILITY',
];

export const webURL = `${window.location.protocol}//${window.location.host}`;

export const ReasonCodesList = [
  {
    trust_dimension_config_type: 'IDENTITY',
    trust_dimension: 'Identity',
    trust_dimension_description:
      'Verification of consumer identity along a holistic matrix of verified identity attributes.',
    trust_dimension_score: 14,
    trust_dimension_reason_code: 'ID04',
    trust_dimension_risk_level: 'MEDIUM_TRUSTED',
    trust_dimension_score_range_HighRisk: '0-8',
    trust_dimension_score_range_MediumRisk: '8-15',
    trust_dimension_score_range_Trusted: '15-19',
    trust_dimension_name:
      'Consumer has not provided bank verification & has consistent identity across network, and has indicators of syndicate activities.',
    feature_sets: [
      {
        name: 'Network-based identity validation',
        description:
          'Verification of consumer identity through the consumer’s bank and across nodes on the network.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 2.6,
        feature_set_score_range_HighRisk: '0-1.5',
        feature_set_score_range_MediumRisk: '1.5-3',
        feature_set_score_range_Trusted: '3-4',
        reason_codes: [
          {
            notable: true,
            code: 'ID1V32',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Verified identity by more than one merchant/issuer and bank authentication.',
            score: 1,
          },
          {
            notable: true,
            code: 'ID1C2',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Consumer identity has partial inconsistencies across the network  (unmatched name, address, date of birth, etc.)',
            score: 1,
          },
          {
            notable: true,
            code: 'ID1N21',
            risk_level: 'MEDIUM_TRUSTED',
            description: 'Customer name is a close match with bank records.',
            score: 1,
          },
        ],
      },
      {
        name: 'Digital identity - email',
        description: 'Evaluation of email activity and usage patterns for credibility.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 1,
        feature_set_score_range_HighRisk: '0-0.3',
        feature_set_score_range_MediumRisk: '0.3-0.7',
        feature_set_score_range_Trusted: '0.7-1',
        reason_codes: [
          {
            notable: true,
            code: 'ID2EC1',
            risk_level: 'TRUSTED',
            description:
              'Email and phone number & address have a consistent match across records.',
            score: 1,
          },
          {
            notable: false,
            code: 'ID2E1',
            risk_level: 'TRUSTED',
            description:
              'Email is from a well-known, established domain (e.g., gmail.com, outlook.com).',
            score: 0,
          },
        ],
      },
      {
        name: 'Digital identity - Device Fingerprinting',
        description:
          'Assessment of digital identity fingerprinting consistency, including device and browser fingerprinting associations with fraudulent activity, and behavioural anomalies across multiple platforms.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 1.4,
        feature_set_score_range_HighRisk: '0-0.7',
        feature_set_score_range_MediumRisk: '0.7-1.5',
        feature_set_score_range_Trusted: '1.5-2',
        reason_codes: [
          {
            notable: true,
            code: 'ID403',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Device or IP is linked to multiple accounts but within a reasonable scope (e.g., workplace or shared househol',
            score: 1,
          },
        ],
      },
      {
        name: 'Digital identity - Browser Fingerprinting',
        description:
          'Assessment of digital identity browser fingerprinting consistency, detects when malicious tools commonly used to spoof devices, such as emulators, are being used.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 0.5,
        feature_set_score_range_HighRisk: '0-0.3',
        feature_set_score_range_MediumRisk: '0.3-0.7',
        feature_set_score_range_Trusted: '0.7-1',
        reason_codes: [
          {
            notable: true,
            code: 'ID603',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Browser fingerprint matches multiple devices or is used inconsistently.',
            score: 0,
          },
          {
            notable: true,
            code: 'IBR03',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Browser attributes (e.g., IP address, device ID, OS version) change frequently within a short period',
            score: 0,
          },
        ],
      },
      {
        name: 'Digital identity - Geolocation',
        description:
          'Assessment of location consistency, shared geolocation patterns, and unusual access points across networks.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 1.4,
        feature_set_score_range_HighRisk: '0-0.7',
        feature_set_score_range_MediumRisk: '0.7-1.5',
        feature_set_score_range_Trusted: '1.5-2',
        reason_codes: [
          {
            notable: true,
            code: 'ID703',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'A single delivery address is used for transactions across multiple identities.',
            score: 0,
          },
          {
            notable: true,
            code: 'ID803',
            risk_level: 'TRUSTED',
            description:
              'The customer’s geolocation does not show expected consistency with past transactions and their address',
            score: 1,
          },
        ],
      },
      {
        name: 'Payment methods',
        description:
          'Evaluation of payment method usage, account links, and inconsistencies in payment and account details to identify potential risks.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 2,
        feature_set_score_range_HighRisk: '0-1',
        feature_set_score_range_MediumRisk: '1-2.2',
        feature_set_score_range_Trusted: '2.2-3',
        reason_codes: [
          {
            notable: true,
            code: 'ID903',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'A single payment card is used across more accounts than the average number of people per household in the area, suggesting syndicate activities.',
            score: 1,
          },
          {
            notable: true,
            code: 'ID402',
            risk_level: 'TRUSTED',
            description:
              ' IBAN positively matches the name and account and is not linked to any reported frauds.',
            score: 1,
          },
        ],
      },
      {
        name: 'Synthetic Identity',
        description:
          'Evaluation of the likelihood the consumer profile is a synthetic identity',
        feature_set_risk_level: 'TRUSTED',
        feature_set_score: 2.5,
        feature_set_score_range_HighRisk: '0-1',
        feature_set_score_range_MediumRisk: '1-2.3',
        feature_set_score_range_Trusted: '2.3-3',
        reason_codes: [
          {
            notable: true,
            code: 'ID901',
            risk_level: 'TRUSTED',
            description:
              'Email, phone, payment method, and address are associated with a single identity.',
            score: 1,
          },
          {
            notable: true,
            code: 'ID901',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Identity attributes are shared across multiple accounts but within a limited geographic or digital cluster',
            score: 1,
          },
        ],
      },
      {
        name: 'Syndicate activity',
        description:
          'Evaluation of likelihood the consumer profile is operating as part of a syndicate or organized crime',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 2.6,
        feature_set_score_range_HighRisk: '0-1',
        feature_set_score_range_MediumRisk: '1-2.3',
        feature_set_score_range_Trusted: '2.3-3',
        reason_codes: [
          {
            notable: true,
            code: 'ID903',
            risk_level: 'TRUSTED',
            description:
              'Account activity and interactions appear typical and independent.',
            score: 1,
          },
          {
            notable: true,
            code: 'ID903',
            risk_level: 'TRUSTED',
            description:
              'Identity attributes (e.g., address, phone number) are shared within a reasonable threshold (e.g., family members in the same household) based on the average number in the customer’s area',
            score: 1,
          },
        ],
      },
    ],
  },
  {
    trust_dimension_config_type: 'CHARGEBACKS',
    trust_dimension: 'Chargebacks',
    trust_dimension_description:
      "Assessment of a consumer's chargeback behaviour across the ecosystem, including frequency and fraud.",
    trust_dimension_score: 12.5,
    trust_dimension_reason_code: 'CB07',
    trust_dimension_risk_level: 'MEDIUM_TRUSTED',
    trust_dimension_score_range_HighRisk: '0-7',
    trust_dimension_score_range_MediumRisk: '7-14',
    trust_dimension_score_range_Trusted: '14-18',
    trust_dimension_name:
      'Consumer exhibits a medium to high frequency of chargebacks (>0.3%) across multiple payment methods and merchants, with chargeback claims generally honoured.',
    feature_sets: [
      {
        name: 'Chargeback history',
        description:
          'Analysis of chargeback rates and reasons to identify fraud trends and customer behavior patterns.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 7,
        feature_set_score_range_HighRisk: '0-4',
        feature_set_score_range_MediumRisk: '4-7.5',
        feature_set_score_range_Trusted: '7.5-10',
        reason_codes: [
          {
            notable: true,
            code: 'CB102',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Consumer has medium chargeback rate (< 0.3%) across multiple payment methods and merchants',
            score: 1,
          },
          {
            notable: true,
            code: 'CB201',
            risk_level: 'TRUSTED',
            description: 'Chargeback claims are generally granted',
            score: 1,
          },
          {
            notable: true,
            code: 'CB303',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Customer has a moderate number of chargebacks concentrated with a specific merchant, potentially due to genuine merchant issues',
            score: 0,
          },
          {
            notable: true,
            code: 'CB403',
            risk_level: 'TRUSTED',
            description: 'Consumer Maintains Stable Spending Behavior After Chargeback',
            score: 2,
          },
          {
            notable: true,
            code: 'CB503',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Device fingerprint in chargeback is new and hasn’t appeared in previous legitimate transactions.',
            score: 0,
          },
          {
            notable: true,
            code: 'CB703',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Consumer chargebacks predominantly related to transactions without 3D secure',
            score: 1,
          },
          {
            notable: true,
            code: 'CB903',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'The customer files a chargeback and then repurchases the same or similar item within a short period, possibly using a different payment method or account.',
            score: 0,
          },
          {
            notable: true,
            code: 'CB123',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'The account has limited prior purchase history or interactions but initiates a high-value transaction, which is later disputed through a chargeback.',
            score: 0,
          },
        ],
      },
      {
        name: 'Dispute and claims history',
        description:
          'Analysis of dispute and claims handling data to identify fraud trends and customer behavior patterns.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 5.5,
        feature_set_score_range_HighRisk: '0-3',
        feature_set_score_range_MediumRisk: '3-6',
        feature_set_score_range_Trusted: '6-8',
        reason_codes: [
          {
            notable: true,
            code: 'CB102',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Consumer activity consistent with genuine personal usage; no signs of syndicate involvement or financial muling (ecommerce and/or financial)',
            score: 1,
          },
          {
            notable: true,
            code: 'CB201',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Consumer claims predominantly on digital goods, luxury items, or high-value transactions.',
            score: 1,
          },
          {
            notable: true,
            code: 'CB303',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Consumer claims concentrated with a specific merchant, could be legitimate merchant issues',
            score: 0,
          },
          {
            notable: true,
            code: 'CB603',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Consumer uses the same dispute reason (e.g. “Item Not Received”) across different merchants',
            score: 0,
          },
          {
            notable: true,
            code: 'CB703',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Consumer chargebacks predominantly related to transactions without 3D secure',
            score: 1,
          },
        ],
      },
    ],
  },
  {
    trust_dimension_config_type: 'RETURNS',
    trust_dimension: 'Returns',
    trust_dimension_description:
      "Evaluation of a consumer's returns and refunds behaviour across the ecosystem, including frequency and fraud.",
    trust_dimension_score: 10,
    trust_dimension_reason_code: 'R01',
    trust_dimension_risk_level: 'MEDIUM_TRUSTED',
    trust_dimension_score_range_HighRisk: '0-6',
    trust_dimension_score_range_MediumRisk: '6-11',
    trust_dimension_score_range_Trusted: '11-15',
    trust_dimension_name:
      'Consumer has medium to high return rate (< x%) across multiple payment methods and merchants, and refunds are generally honoured.',
    feature_sets: [
      {
        name: 'Refund History',
        description: 'Evaluation of refund outcomes to assess risk tendencies.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 5.5,
        feature_set_score_range_HighRisk: '0-3',
        feature_set_score_range_MediumRisk: '3-6',
        feature_set_score_range_Trusted: '6-8',
        reason_codes: [
          {
            notable: true,
            code: 'BR03',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Consumer has medium return rate (< 30%) across multiple payment methods and merchants – refunds are generally honoured',
            score: 0,
          },
          {
            notable: true,
            code: 'BR1V1',
            risk_level: 'MEDIUM_TRUSTED',
            description: 'Stable refund frequency and average refund value over time',
            score: 0,
          },
          {
            notable: true,
            code: 'BR11',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Consistent and legitimate reasons provided for returns, with prompt return timings',
            score: 0,
          },
          {
            notable: true,
            code: 'BR05',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Customer returns items whose value closely matches the remaining balance in their bank account',
            score: 0,
          },
          {
            notable: true,
            code: 'BR8',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Moderate refund frequency and value with variable durations between refund requests',
            score: 0,
          },
        ],
      },
      {
        name: 'Return History',
        description: 'Evaluation of return outcomes to assess risk tendencies.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 4.5,
        feature_set_score_range_HighRisk: '0-3',
        feature_set_score_range_MediumRisk: '3-5',
        feature_set_score_range_Trusted: '5-7',
        reason_codes: [
          {
            notable: true,
            code: 'NR03',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'The customer consistently uses the same refund reasons and timing across different merchants, indicating a repeated fraud pattern',
            score: 0,
          },
          {
            notable: true,
            code: 'NR032',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Returns consistently initiated from known, consistent locations',
            score: 0,
          },
        ],
      },
    ],
  },
  {
    trust_dimension_config_type: 'CLAIMS_OF_ITEMS_NOT_RECEIVED',
    trust_dimension: 'Claims of Items-not-received',
    trust_dimension_description:
      "Evaluation of a consumer's history with claimed or factual delivery issues, and instances of recurring Item Not Received (INR) claims.",
    trust_dimension_score: 5,
    trust_dimension_reason_code: 'SHIP1',
    trust_dimension_risk_level: 'MEDIUM_TRUSTED',
    trust_dimension_score_range_HighRisk: '0-3',
    trust_dimension_score_range_MediumRisk: '3-6',
    trust_dimension_score_range_Trusted: '6-8',
    trust_dimension_name:
      'Consumer has medium return rate across multiple payment methods and merchants, and refunds are. generally honoured.',
    feature_sets: [
      {
        name: 'Claim History',
        description:
          'Evaluation of shipping and delivery patterns to identify risks associated with delivery issues',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 2.5,
        feature_set_score_range_HighRisk: '0-1',
        feature_set_score_range_MediumRisk: '1-2.3',
        feature_set_score_range_Trusted: '2.3-3',
        reason_codes: [
          {
            notable: true,
            code: 'SHIP05',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Consumer has medium INR claim rate (< x%) across multiple payment methods and merchants – claims are generally honoured',
            score: 0,
          },
        ],
      },
      {
        name: 'Shipping and delivery',
        description:
          'Evaluation of shipping and delivery patterns to identify risks associated with delivery issues',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 2,
        feature_set_score_range_HighRisk: '0-1',
        feature_set_score_range_MediumRisk: '1-2.2',
        feature_set_score_range_Trusted: '2.2-3',
        reason_codes: [
          {
            notable: true,
            code: 'SHIP05',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Late Delivery vs. Expected Time—significant delay between expected and actual delivery time, increasing INR (Item Not Received) claims.',
            score: 0,
          },
          {
            notable: true,
            code: 'SHIP05',
            risk_level: 'MEDIUM_TRUSTED',
            description: 'Claims consistently initiated from known, consistent locations',
            score: 0,
          },
        ],
      },
      {
        name: 'Historical Fraud Indicators',
        description:
          'Evaluation of shipping and delivery patterns to identify risks associated with delivery issues',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 1,
        feature_set_score_range_HighRisk: '0-0.7',
        feature_set_score_range_MediumRisk: '0.7-1.5',
        feature_set_score_range_Trusted: '1.5-2',
        reason_codes: [
          {
            notable: true,
            code: 'SHIP05',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Customer maintains a regular pattern in inflows and outflows without sudden spikes',
            score: 0,
          },
        ],
      },
    ],
  },
  {
    trust_dimension_config_type: 'BNPL',
    trust_dimension: 'BNPL',
    trust_dimension_description:
      "Assessment of a consumer's patterns of BNPL coverage, defaults, and ghosting across the ecosystem.",
    trust_dimension_score: 5.2,
    trust_dimension_reason_code: 'BNPL1',
    trust_dimension_risk_level: 'MEDIUM_TRUSTED',
    trust_dimension_score_range_HighRisk: '0-3',
    trust_dimension_score_range_MediumRisk: '3-6',
    trust_dimension_score_range_Trusted: '6-8',
    trust_dimension_name:
      ' Consumer has consistent BNPL orders with timely payments, along with stable income and regular use of the same trusted identity.',
    feature_sets: [
      {
        name: 'BNPL affordability and exposure',
        description:
          'BNPL affordability and exposure, synthetic data in application, consumer ghosting and bust out behavior',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 2.5,
        feature_set_score_range_HighRisk: '0-1.6',
        feature_set_score_range_MediumRisk: '1.6-3',
        feature_set_score_range_Trusted: '3-4',
        reason_codes: [
          {
            notable: true,
            code: 'BNPL01',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'High BNPL exposure without corresponding deposits, suggesting possible affordability issues',
            score: 0,
          },
          {
            notable: true,
            code: 'BNPL05',
            risk_level: 'MEDIUM_HIGH_RISK',
            description: 'Consumer has multiple loans but maintains timely payments.',
            score: 0,
          },
        ],
      },
      {
        name: 'Synthetic data in application',
        description:
          'BNPL affordability and exposure, synthetic data in application, consumer ghosting and bust out behavior',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 1.4,
        feature_set_score_range_HighRisk: '0-0.7',
        feature_set_score_range_MediumRisk: '0.7-1.5',
        feature_set_score_range_Trusted: '1.5-2',
        reason_codes: [
          {
            notable: true,
            code: 'BNPL01',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Same device used to log into multiple accounts, but with moderate purchase amounts',
            score: 0,
          },
          {
            notable: true,
            code: 'BNPL05',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Transactions consistently initiated from known, consistent locations',
            score: 0,
          },
        ],
      },
      {
        name: 'Refund and Return Behaviors',
        description:
          'BNPL affordability and exposure, synthetic data in application, consumer ghosting and bust out behavior',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 1.3,
        feature_set_score_range_HighRisk: '0-0.7',
        feature_set_score_range_MediumRisk: '0.7-1.5',
        feature_set_score_range_Trusted: '1.5-2',
        reason_codes: [
          {
            notable: true,
            code: 'BNPL01',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Consumer has a mederate rate of denied refunds across different merchants',
            score: 0,
          },
        ],
      },
    ],
  },
  {
    trust_dimension_config_type: 'AUTHORISED_PUSH_PAYMENTS',
    trust_dimension: 'Authorised Push Payments',
    trust_dimension_description:
      "Evaluation of the consumer's association with claims of scamming or APP fraud",
    trust_dimension_score: 6,
    trust_dimension_reason_code: 'APP1',
    trust_dimension_risk_level: 'MEDIUM_TRUSTED',
    trust_dimension_score_range_HighRisk: '0-3',
    trust_dimension_score_range_MediumRisk: '3-6.5',
    trust_dimension_score_range_Trusted: '6.5-9',
    trust_dimension_name:
      'Consumer maintains a consistent list of recipients and payees over time.',
    feature_sets: [
      {
        name: 'Payer risk for APP fraud',
        description: 'Payer cash flow and payments',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 3,
        feature_set_score_range_HighRisk: '0-1.6',
        feature_set_score_range_MediumRisk: '1.6-3',
        feature_set_score_range_Trusted: '3-4',
        reason_codes: [
          {
            notable: true,
            code: 'APP02',
            risk_level: 'MEDIUM_TRUSTED',
            description: 'Minor mismatch in identity verification across network',
            score: 0,
          },
          {
            notable: true,
            code: 'APP02',
            risk_level: 'MEDIUM_TRUSTED',
            description:
              'Customer maintains a regular pattern in inflows and outflows without sudden spikes',
            score: 0,
          },
        ],
      },
      {
        name: 'Payee risk for APP fraud',
        description: 'Payee cash flow and payments',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 3,
        feature_set_score_range_HighRisk: '0-2',
        feature_set_score_range_MediumRisk: '2-3.7',
        feature_set_score_range_Trusted: '3.7-5',
        reason_codes: [
          {
            notable: true,
            code: 'APP02',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'High-volume peer-to-peer transfers without clear transactional purpose, indicating potential fraud or mule account activity.',
            score: 0,
          },
          {
            notable: true,
            code: 'APP02',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Spending amount remains consistently low in proportion to spending',
            score: 0,
          },
        ],
      },
    ],
  },
  {
    trust_dimension_config_type: 'MULING',
    trust_dimension: 'Muling',
    trust_dimension_description:
      'Evaluation of purchasing and transaction patterns to identify risks associated with financial and ecommerce muling.',
    trust_dimension_score: 6,
    trust_dimension_reason_code: 'ML01',
    trust_dimension_risk_level: 'TRUSTED',
    trust_dimension_score_range_HighRisk: '0-3',
    trust_dimension_score_range_MediumRisk: '3-5.3',
    trust_dimension_score_range_Trusted: '5.3-7',
    trust_dimension_name:
      ' Consumer has verified and long-standing profile with no sign of funnel accounts, along with stable income and regular use of the same trusted identity',
    feature_sets: [
      {
        name: 'E-commerce Muling',
        description:
          'Evaluation of purchasing and shipping patterns to identify risks associated with fraudulent order fulfillment and reshipping activities.',
        feature_set_risk_level: 'TRUSTED',
        feature_set_score: 3,
        feature_set_score_range_HighRisk: '0-1.2',
        feature_set_score_range_MediumRisk: '1.2-2.5',
        feature_set_score_range_Trusted: '2.5-3.5',
        reason_codes: [
          {
            notable: true,
            code: 'EM102',
            risk_level: 'TRUSTED',
            description:
              'Consumer has verified and long-standing profile with no sign of funnel account',
            score: 0,
          },
        ],
      },
      {
        name: 'Financial Muling',
        description:
          'Evaluation of transaction flow patterns to identify risks associated with suspicious fund transfers and potential money laundering activities.',
        feature_set_risk_level: 'MEDTRUSTEDIUM_TRUSTED',
        feature_set_score: 3,
        feature_set_score_range_HighRisk: '0-1.2',
        feature_set_score_range_MediumRisk: '1.2-2.5',
        feature_set_score_range_Trusted: '2.5-3.5',
        reason_codes: [
          {
            notable: true,
            code: 'EM204',
            risk_level: 'TRUSTED',
            description:
              'Consumer has stable income and regular use of the same trusted identity',
            score: 0,
          },
        ],
      },
    ],
  },
  {
    trust_dimension_config_type: 'PROMOTION_BONUS_AND_LOYALTY_ABUSE',
    trust_dimension: 'Promotion, Bonus & Loyalty Abuse',
    trust_dimension_description:
      'Evaluation of purchasing and transaction patterns to identify risks associated with financial and ecommerce muling.',
    trust_dimension_score: 4.5,
    trust_dimension_reason_code: 'PA01',
    trust_dimension_risk_level: 'TRUSTED',
    trust_dimension_score_range_HighRisk: '0-2',
    trust_dimension_score_range_MediumRisk: '2-3.6',
    trust_dimension_score_range_Trusted: '3.6-5',
    trust_dimension_name:
      'Consumer has consistent identity across network, with frequency of promo usage that falls within terms, possibly opportunistic.',
    feature_sets: [
      {
        name: 'Account Creation and Identity',
        description:
          'Monitoring for fraudulent account creation using fake or duplicate identities to exploit policies.',
        feature_set_risk_level: 'TRUSTED',
        feature_set_score: 1.9,
        feature_set_score_range_HighRisk: '0-0.7',
        feature_set_score_range_MediumRisk: '0.7-1.5',
        feature_set_score_range_Trusted: '1.5-2',
        reason_codes: [
          {
            notable: true,
            code: 'PB101',
            risk_level: 'TRUSTED',
            description:
              'Consumer has verified and long-standing profile with no sign of funnel accounts.',
            score: 0,
          },
        ],
      },
      {
        name: 'Transaction Behavior Risk Features',
        description:
          'Analyzing transaction patterns to detect suspicious behaviors, such as rapid bonus redemptions, abnormal purchases, or frequent small transactions.',
        feature_set_risk_level: 'TRUSTED',
        feature_set_score: 1.6,
        feature_set_score_range_HighRisk: '0-0.7',
        feature_set_score_range_MediumRisk: '0.7-1.5',
        feature_set_score_range_Trusted: '1.5-2',
        reason_codes: [
          {
            notable: true,
            code: 'PB203',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Points earned from transactions that were accepted my multiple merchants using consistent identity across network',
            score: 0,
          },
        ],
      },
      {
        name: 'Loyalty Program Abuse',
        description:
          'Identifying misuse of loyalty programs, including unauthorized point accumulation, multiple redemptions through fake or duplicate accounts.',
        feature_set_risk_level: 'TRUSTED',
        feature_set_score: 1,
        feature_set_score_range_HighRisk: '0-0.3',
        feature_set_score_range_MediumRisk: '0.3-0.7',
        feature_set_score_range_Trusted: '0.7-1',
        reason_codes: [
          {
            notable: true,
            code: 'PB401',
            risk_level: 'TRUSTED',
            description:
              'User claiming bonuses across multiple merchants using consistent credentials.',
            score: 0,
          },
        ],
      },
    ],
  },
  {
    trust_dimension: 'Affordability and Payments',
    trust_dimension_description:
      "Assessment of a consumer's propensity towards first-party fraud based on affordability, payments and banking behaviours.",
    trust_dimension_score: 7.5,
    trust_dimension_reason_code: 'BP1',
    trust_dimension_risk_level: 'MEDIUM_TRUSTED',
    trust_dimension_score_range_HighRisk: '0-4',
    trust_dimension_score_range_MediumRisk: '4-8',
    trust_dimension_score_range_Trusted: '8-11',
    trust_dimension_name:
      'Consumer has stable income, with a notable decrease/increase in shopping behavior compared to the previous Customer Trust Index ',
    feature_sets: [
      {
        name: 'Affordability',
        description:
          'Indicators of purchasing power that may signal potential risk for all fraud types',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 3.5,
        feature_set_score_range_HighRisk: '0-2',
        feature_set_score_range_MediumRisk: '2-3.7',
        feature_set_score_range_Trusted: '3.7-5',
        reason_codes: [
          {
            notable: true,
            code: 'BAF01',
            risk_level: 'TRUSTED',
            description: 'The customer has stable income over the past 3 months',
            score: 0,
          },
        ],
      },
      {
        name: 'Order History',
        description:
          "Overview of transaction patterns within the consumer's purchase history.",
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 2,
        feature_set_score_range_HighRisk: '0-1',
        feature_set_score_range_MediumRisk: '1-2.2',
        feature_set_score_range_Trusted: '2.2-3',
        reason_codes: [
          {
            notable: true,
            code: 'BOH03',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Significant average order value compared to account balance, increasing the risk of transaction authenticity issues.',
            score: 0,
          },
          {
            notable: true,
            code: 'BOH04',
            risk_level: 'MEDIUM_HIGH_RISK',
            description: 'Transactions occur regularly during standard hours of the day.',
            score: 1,
          },
          {
            notable: true,
            code: 'BOH06',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Transactional behavior aligns with account history and expected patterns.',
            score: 0,
          },
        ],
      },
      {
        name: 'Bank Activity',
        description:
          'Analysis of financial transaction behaviours for signs of unusual activity.',
        feature_set_risk_level: 'MEDIUM_TRUSTED',
        feature_set_score: 2,
        feature_set_score_range_HighRisk: '0-1',
        feature_set_score_range_MediumRisk: '1-2.2',
        feature_set_score_range_Trusted: '2.2-3',
        reason_codes: [
          {
            notable: true,
            code: 'BA02',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Incoming flows are moderately stable with occasional fluctuations',
            score: 2,
          },
          {
            notable: true,
            code: 'BA05',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Outgoing flows are moderately stable with occasional fluctuations',
            score: 3,
          },
          {
            notable: false,
            code: 'BA07',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'Funds received from multiple sources, some irregular activity detected.',
            score: 0,
          },
          {
            notable: true,
            code: 'BP03',
            risk_level: 'MEDIUM_HIGH_RISK',
            description:
              'No transactions are recorded involving low-traceability accounts',
            score: 3,
          },
        ],
      },
    ],
  },
];

export const ConsoleSessionEvent = {
  TOTAL_CONSOLE_SESSIONS: 'total',
  CTI_PROFILE_VIEW: 'cti_profile_view',
  PROJECT_EDIT: 'project_edit',
  PROJECT_VIEW: 'project_view',
  DASHBOARD_OVERVIEW_VIEW: 'dashboard_overview_view',
  DASHBOARD_CONSUMERS_VIEW: 'dashboard_consumers_view',
  DASHBOARD_ACCURACY_VIEW: 'dashboard_accuracy_view',
  DASHBOARD_PERFORMANCE_VIEW: 'dashboard_performance_view',
  DATA_UPLOAD: 'data_upload',
};

export const API_CALL_ENDPOINTS = {
  TOTAL_API_CALLS: {
    label: 'Total API Calls',
    value: 'total',
  },
  GET_CTI_RETRIEVAL: {
    label: 'Get Consumer Trust Index (Retrieval)',
    value: '/api/getctiretrieval',
  },
  GET_CTI: {
    label: 'Get Consumer Trust Index (Calculation)',
    value: '/api/getcti',
  },
  GET_CTI_HISTORY: {
    label: 'Get Consumer Trust Index history',
    value: '/api/getctihistory',
  },
  CTI_UPDATE_FILE_UPLOAD: {
    label: 'Calculate Consumer Trust Index via file upload',
    value: '/api/ctiupdate/fileupload',
  },
  CTI_UPDATE_SFTP: {
    label: 'Calculate Consumer Trust Index via Open Banking',
    value: '/api/ctiupdate/sftp',
  },
  CTI_UPDATE_OPEN_BANKING: {
    label: 'Calculate Consumer Trust Index via SFTP',
    value: '/api/ctiupdate/openbanking',
  },
  DATA_UPLOAD: {
    label: 'Calculate Consumer Trust Index via API',
    value: '/api/dataupload',
  },
};

export const RecommendedActions = {
  LOW_RISK: {
    action: 'Allow',
    icon: AllowIcon,
  },
  MEDIUM_RISK: {
    action: 'Restrict',
    icon: RestrictIcon,
  },
  HIGH_RISK: {
    action: 'Block',
    icon: BlockIcon,
  },
};

export const SignificantSignalsList = [
  {
    signal: 'Chargeback frequency',
    description: 'Timings between individual requests and transaction dates',
    code: 'CHB1FRE',
    reasons: [
      {
        risk_level: 'High trust',
        code: 'CHB1FRE1',
        probability: 0.15,
        description:
          'Consumer initiates chargebacks infrequently, with disputes filed within expected delivery or resolution windows — neither immediately after purchase nor concentrated near issuer or scheme filing deadlines — indicating credible and policy-aligned behaviour.',
      },
      {
        risk_level: 'Medium trust',
        code: 'CHB1FRE2',
        probability: 0.25,
        description:
          'Consumer shows a shortening interval between transactions and chargebacks or increasing clustering of disputes close to issuer or scheme filing deadlines when compared to their historical behaviour, suggesting emerging opportunistic use of chargebacks.',
      },
      {
        risk_level: 'Low trust',
        code: 'CHB1FRE3',
        probability: 0.6,
        description:
          'Consumer persistently files chargebacks either very shortly after transactions or repeatedly just before issuer or scheme filing deadlines across merchants and over multiple periods, indicating strategic exploitation of dispute timing and elevated fraud risk.',
      },
    ],
  },
  {
    signal: 'Account syndicate link',
    description: 'Association of profile with identified fraud syndicates',
    code: 'IDN3ASL',
    reasons: [
      {
        risk_level: 'High trust',
        code: 'IDN3ASL1',
        probability: 0.15,
        description:
          'Consumer profile demonstrates no relationship with profiles in identified fraud syndicates.',
      },
      {
        risk_level: 'Medium trust',
        code: 'IDN3ASL2',
        probability: 0.25,
        description: '-',
      },
      {
        risk_level: 'Low trust',
        code: 'IDN3ASL3',
        probability: 0.6,
        description:
          'Consumer profile demonstrates relationship with profiles in identified fraud syndicates.',
      },
    ],
  },
  {
    signal: 'Linkage to multiple refund-heavy profiles',
    description:
      'Association of profile (on basis of shared devices/address/payment methods) to profile demonstrating return and refund abuse.',
    code: 'RET1RHP',
    reasons: [
      {
        risk_level: 'High trust',
        code: 'RET1RHP1',
        probability: 0.15,
        description:
          'Consumer shows no associations, on the basis of shared devices, addresses, or payment methods, to profiles demonstrating refund or return abuse – indicating isolated account activity.',
      },
      {
        risk_level: 'Medium trust',
        code: 'RET1RHP2',
        probability: 0.25,
        description:
          'Consumer shows associations, on the basis of shared devices, addresses, or payment methods, to one or more profiles with emerging refund and return abuse patterns.',
      },
      {
        risk_level: 'Low trust',
        code: 'RET1RHP3',
        probability: 0.6,
        description:
          'Consumer demonstrates persistent associations, on the basis of shared devices, addresses, or payment methods, to multiple profiles with confirmed refund and return abuse patterns – strongly indicating coordinated abuse and elevated fraud risk.',
      },
    ],
  },
];

export const RISK_LEVEL_MAP: Record<config_type, string> = {
  LOW_RISK: 'High trust',
  MEDIUM_RISK: 'Medium trust',
  HIGH_RISK: 'Low trust',
};

export const RISK_MAP = {
  HIGH_RISK: ['LOW'],
  MEDIUM_RISK: ['MEDIUM'],
  LOW_RISK: ['HIGH', 'POSITIVE'],
};

export const ACTION_MAP = {
  HIGH_RISK: 'DECLINE',
  MEDIUM_RISK: 'REVIEW',
  LOW_RISK: 'APPROVE',
};

export const RecommendedActionMapping = {
  ALLOW: 'Allow',
  REVIEW: 'Review',
  BLOCK: 'Block',
  INSTANT_REFUND: 'Instant refund',
  REFUND_AFTER_RETURN: 'Refund after return',
  HOLD: 'Hold',
  APPROVE: 'Approve',
  DECLINE: 'Decline',
};

export const RecommenationSignalsData = {
  event_types: {
    onboarding: 'onboarding',
    'pre-authorisation': 'pre_auth',
    'post-authorisation': 'post_purchase',
  },
  event_rules: {
    onboarding: ['R1', 'R3', 'R8', 'R14'],
    pre_auth: [
      'R1',
      'R2',
      'R5',
      'R6',
      'R7',
      'R9',
      'R10',
      'R11',
      'R12',
      'R13',
      'R14',
      'R15',
      // 'R17',
    ], // Hide R17 for now
    post_purchase: ['R4', 'R11', 'R12', 'R15', 'R16', 'R18'], // Hide R17 for Now
  },
  action_mapping: {
    onboarding: {
      APPROVE: 'ALLOW',
      REVIEW: 'REVIEW',
      DECLINE: 'BLOCK',
    },
    pre_auth: {
      APPROVE: 'APPROVE',
      REVIEW: 'REVIEW',
      DECLINE: 'DECLINE',
    },
    post_purchase: {
      APPROVE: 'INSTANT_REFUND',
      REVIEW: 'REFUND_AFTER_RETURN',
      DECLINE: 'HOLD',
    },
  },
  signals: {
    IDN2SID: {
      description:
        'Shared or reused identity attributes across multiple consumer profiles.',
      dimension: 'Identity',
    },
    IDN3ASL: {
      description: 'Profile associated with known identity or account-level syndicates.',
      dimension: 'Identity',
    },
    IDN3CFA: {
      description: 'Behaviour matches coordinated fraud attack clusters.',
      dimension: 'Identity',
    },
    IDN4RFC: {
      description: 'Card or account linked to past first-party fraud reports.',
      dimension: 'Identity',
    },
    IDN4SPM: {
      description: 'Payment method shared across multiple accounts.',
      dimension: 'Identity',
    },

    CHB5PAT: {
      description:
        'Chargeback pattern/rationale matches cross-merchant behavioural clustering.',
      dimension: 'Chargeback Trends',
    },
    CHB1FRE: {
      description: 'Elevated chargeback frequency.',
      dimension: 'Chargeback Trends',
    },

    AFP1FRT: {
      description: 'Failed/reversed transaction tendency across payment methods.',
      dimension: 'Payments & Affordability',
    },
    AFP2LTP: {
      description: 'Refund or spend routed via low-traceability channels.',
      dimension: 'Payments & Affordability',
    },
    LTP1NUM: {
      description: 'High count of low-traceability transactions.',
      dimension: 'Payments & Affordability',
    },
  },
  reason_codes: {
    IDN2SID0: 'Identity shared across accounts (Low significance)',
    IDN2SID1: 'Identity shared across accounts (Medium significance)',
    IDN2SID2: 'Identity shared across accounts (High significance)',

    IDN4RFC0: 'Past fraud linkage detected (Low significance)',
    IDN4RFC1: 'Past fraud linkage detected (Medium significance)',
    IDN4RFC2: 'Past fraud linkage detected (High significance)',

    AFP1FRT0: 'Failed/reversed transaction pattern (Low significance)',
    AFP1FRT1: 'Failed/reversed transaction pattern (Medium significance)',
    AFP1FRT2: 'Failed/reversed transaction pattern (High significance)',

    LTP1NUM0: 'Low-traceability transaction count (Low significance)',
    LTP1NUM1: 'Low-traceability transaction count (Medium significance)',
    LTP1NUM2: 'Low-traceability transaction count (High significance)',

    CHB5PAT0: 'Refund/chargeback clustering pattern (Low significance)',
    CHB5PAT1: 'Refund/chargeback clustering pattern (Medium significance)',
    CHB5PAT2: 'Refund/chargeback clustering pattern (High significance)',
  },
  rules: [
    {
      id: 'R1',
      event_types: ['onboarding', 'pre_auth'],
      action: 'APPROVE',
      cti_band: ['HIGH'],
      conditions: {
        signals: ['IDN4RFC'],
        behavioural: [],
      },
      triggered_reason_codes: ['IDN4RFC1'],
      intent:
        'Auto-approve high-trust users with stable identity and no negative evidence.',
    },
    {
      id: 'R2',
      event_types: ['pre_auth'],
      action: 'REVIEW',
      cti_band: ['MEDIUM'],
      conditions: {
        signals: ['AFP1FRT', 'LTP1NUM'],
        behavioural: [{ field: 'txn_velocity_24h', op: 'gt', value: 3 }],
      },
      triggered_reason_codes: ['AFP1FRT2', 'LTP1NUM2'],
      intent:
        'Flag mid-trust users with abnormal short-term velocity and predictive payment-risk signals.',
    },
    {
      id: 'R3',
      event_types: ['onboarding', 'pre_auth'],
      action: 'DECLINE',
      cti_band: ['LOW'],
      conditions: {
        signals: ['IDN2SID', 'IDN4SPM'],
        behavioural: [],
      },
      triggered_reason_codes: ['IDN2SID3', 'IDN4SPM3'],
      intent:
        'Block users exhibiting shared identity or payment credentials indicative of account linkage fraud.',
    },
    {
      id: 'R4',
      event_types: ['post_purchase'],
      action: 'DECLINE',
      cti_band: ['LOW'],
      conditions: {
        signals: ['CHB5PAT'],
        behavioural: [],
      },
      triggered_reason_codes: ['CHB5PAT3'],
      intent: 'Decline refund/chargeback behaviour that matches known abuse clusters.',
    },
    {
      id: 'R5',
      event_types: ['pre_auth'],
      action: 'APPROVE',
      cti_band: ['HIGH'],
      conditions: {
        signals: ['AFP2LTP'],
        behavioural: [],
      },
      triggered_reason_codes: ['AFP2LTP1'],
      intent:
        'Approve high-trust consumers even when low-traceability channels appear at low significance.',
    },
    {
      id: 'R6',
      event_types: ['pre_auth'],
      action: 'REVIEW',
      cti_band: ['MEDIUM'],
      conditions: {
        signals: ['IDN3ASL'],
        behavioural: [],
      },
      triggered_reason_codes: ['IDN3ASL2'],
      intent: 'Route to review when syndicate adjacency appears at medium significance.',
    },
    {
      id: 'R7',
      event_types: ['pre_auth'],
      action: 'DECLINE',
      cti_band: ['LOW'],
      conditions: {
        signals: ['IDN3ASL', 'IDN3CFA'],
        behavioural: [],
      },
      triggered_reason_codes: ['IDN3ASL3', 'IDN3CFA3'],
      intent:
        'Decline when identity behaviour matches high-significance coordinated fraud patterns.',
    },
    {
      id: 'R8',
      event_types: ['onboarding'],
      action: 'DECLINE',
      cti_band: ['LOW'],
      conditions: {
        signals: ['IDN2SID'],
        behavioural: [],
      },
      triggered_reason_codes: ['IDN2SID3'],
      intent: 'Reject onboarding where identity reuse shows high predictive fraud risk.',
    },
    {
      id: 'R9',
      event_types: ['pre_auth'],
      action: 'APPROVE',
      cti_band: ['HIGH'],
      conditions: {
        signals: ['AFP1FRT'],
        behavioural: [],
      },
      triggered_reason_codes: ['AFP1FRT1'],
      intent:
        'Approve when failed-transaction behaviour appears but is low significance on a high-trust user.',
    },
    {
      id: 'R10',
      event_types: ['pre_auth'],
      action: 'REVIEW',
      cti_band: ['MEDIUM'],
      conditions: {
        signals: ['IDN4RFC'],
        behavioural: [{ field: 'device_history_days', op: 'lt', value: 5 }],
      },
      triggered_reason_codes: ['IDN4RFC2'],
      intent:
        'Trigger review for mid-trust users with new/short device history and medium-significance identity risk.',
    },
    // REVIEW should only emit suffix 2 codes (no suffix 1 codes in LOW band displays)
    {
      id: 'R11',
      event_types: ['pre_auth', 'post_purchase'],
      action: 'REVIEW',
      cti_band: ['MEDIUM'],
      conditions: {
        signals: ['CHB1FRE'],
        behavioural: [],
      },
      triggered_reason_codes: ['CHB1FRE2'],
      intent: 'Review when chargeback frequency signal shows medium predictive lift.',
    },
    // DECLINE should only emit suffix 3 codes
    {
      id: 'R12',
      event_types: ['pre_auth', 'post_purchase'],
      action: 'DECLINE',
      cti_band: ['LOW'],
      conditions: {
        signals: ['CHB1FRE'],
        behavioural: [{ field: 'card_decline_streak', op: 'gte', value: 3 }],
      },
      triggered_reason_codes: ['CHB1FRE3'],
      intent:
        'Decline when high-frequency chargebacks combine with sustained card-decline streaks.',
    },
    {
      id: 'R13',
      event_types: ['pre_auth'],
      action: 'DECLINE',
      cti_band: ['LOW'],
      conditions: {
        signals: ['AFP1FRT'],
        behavioural: [{ field: 'session_duration_sec', op: 'lt', value: 5 }],
      },
      triggered_reason_codes: ['AFP1FRT3'],
      intent:
        'Block low-trust users exhibiting high-significance payment instability with near-zero behavioural session signal.',
    },
    {
      id: 'R14',
      event_types: ['onboarding', 'pre_auth'],
      action: 'REVIEW',
      cti_band: ['MEDIUM'],
      conditions: {
        signals: ['IDN4SPM'],
        behavioural: [],
      },
      triggered_reason_codes: ['IDN4SPM2'],
      intent: 'Review when shared payment method behaviour presents moderate risk.',
    },
    // REVIEW should only emit suffix 2 codes
    {
      id: 'R15',
      event_types: ['post_purchase', 'pre_auth'],
      action: 'REVIEW',
      cti_band: ['MEDIUM'],
      conditions: {
        signals: ['CHB5PAT'],
        behavioural: [],
      },
      triggered_reason_codes: ['CHB5PAT2'],
      intent:
        'Review when chargeback clustering behaviour appears at medium significance.',
    },
    // DECLINE should only emit suffix 3 codes
    {
      id: 'R16',
      event_types: ['post_purchase'],
      action: 'DECLINE',
      cti_band: ['LOW'],
      conditions: {
        signals: ['AFP2LTP'],
        behavioural: [],
      },
      triggered_reason_codes: ['AFP2LTP3'],
      intent:
        'Decline when refund/transaction patterns show high-significance low-traceability indicators.',
    },
    {
      id: 'R17',
      event_types: ['pre_auth', 'post_purchase'],
      action: 'APPROVE',
      cti_band: ['HIGH'],
      conditions: {
        signals: [],
        behavioural: [{ field: 'cti_trend_delta_3m', op: 'gte', value: 0.25 }],
      },
      triggered_reason_codes: [],
      intent:
        'Approve when long-term trust trend is strongly positive, regardless of transient behaviour.',
    },
    // High trust post_purchase rule, evaluated before R17
    {
      id: 'R18',
      event_types: ['post_purchase'],
      action: 'APPROVE',
      cti_band: ['HIGH'],
      conditions: {
        signals: ['CHB1FRE', 'CHB5PAT', 'AFP2LTP'],
        behavioural: [],
      },
      triggered_reason_codes: ['CHB1FRE1', 'CHB5PAT1', 'AFP2LTP1'],
      intent:
        'Approve high-trust post-purchase consumers when post-purchase signals appear only at low significance.',
    },
  ],
};

export const top10Options: Option[] = [
  { label: 'Top 10%', value: 10 },
  { label: 'Top 5%', value: 5 },
  { label: 'Top 1%', value: 1 },
];

export const topOverallOptions: Option[] = [
  { label: 'Overall', value: 'overall' },
  { label: 'Top 10%', value: 10 },
  { label: 'Top 5%', value: 5 },
  { label: 'Top 1%', value: 1 },
];
